/**
 * noIndex.ts — keep ONE page out of search results, for as long as it is open.
 *
 * Owner repo: @thegpservice/gpsclinics-portal. Slice: public clinical-safety
 * page (2026-08-20).
 *
 * WHY THIS EXISTS AT ALL. The portal has no head/meta machinery: `index.html`
 * carries a single static `<title>` and the SPA never touches the document head.
 * That was fine while every public route was category-level copy. The public
 * clinical-safety page is not — it names prescription-only medicines, and it is
 * lawful as a page a patient is SENT to (from a dispatch email, from the payment
 * screen, from a clinician) rather than one they are ACQUIRED through. A search
 * result for a POM brand name pointing at a clinic that sells it is an advert
 * for a prescription-only medicine, whatever the page body says.
 *
 * So this is the minimum that actually works for a client-rendered SPA, and no
 * more: the meta tag goes into the head while the page is mounted and comes back
 * out when it unmounts.
 *
 * WHY A MOUNTED TAG AND NOT A STATIC ONE IN index.html: `index.html` is served
 * for EVERY route (nginx's SPA fallback), so a robots tag written there would
 * de-index the whole portal — including the clinic journey, which marketing
 * depends on. The tag has to be as specific as the page.
 *
 * WHY IT IS REMOVED ON UNMOUNT: this is a single-page app, so a visitor who
 * navigates from this page to the clinic journey keeps the same document. A tag
 * left behind would silently make the next route look de-indexable to any
 * crawler that renders JavaScript — an invisible bug that would surface weeks
 * later as a traffic drop nobody could trace back to a page they never visited.
 *
 * WHAT THIS IS NOT. It is not a security control, and nothing that must not be
 * public may rely on it: a robots directive is a request, honoured by the major
 * engines and ignorable by anything else. The reason it is SAFE to publish this
 * page is that its content is clinical information with no offer attached (see
 * safety/ClinicalSafetyPage.tsx) — the tag only keeps it off a search results
 * page it has no business being on.
 */
import { useEffect } from 'react';

/** The directive. `nofollow` as well as `noindex`: the page links out to the
 * NHS and the MHRA, and we neither want nor deserve to pass ranking signal to
 * them from a page that is not meant to be crawled in the first place. */
export const NOINDEX_CONTENT = 'noindex, nofollow';

/** Our own marker, so we only ever remove a tag WE added — never a future
 * static one somebody adds to index.html for a different reason. */
const OWNED_ATTRIBUTE = 'data-gps-noindex';

/**
 * While the calling component is mounted, ask search engines not to index this
 * page. Idempotent: a second mount reuses the tag already in the head rather
 * than adding a duplicate (React 18 StrictMode mounts effects twice in dev, and
 * two contradictory robots tags is an ambiguity no crawler has to resolve our
 * way).
 */
export function useNoIndex(): void {
  // NO `typeof document` GUARD, deliberately. Effects do not run during server
  // rendering, so the only environments this body ever executes in are ones with
  // a document. A guard here would be an untestable branch that reads as if it
  // were protecting something.
  useEffect(() => {
    const existing = document.head.querySelector<HTMLMetaElement>(`meta[${OWNED_ATTRIBUTE}]`);
    if (existing) return;
    const meta = document.createElement('meta');
    meta.setAttribute('name', 'robots');
    meta.setAttribute('content', NOINDEX_CONTENT);
    meta.setAttribute(OWNED_ATTRIBUTE, 'true');
    document.head.appendChild(meta);
    return () => {
      meta.remove();
    };
  }, []);
}
